/**
 * RoomBar — the room, as it appears over a book somebody is reading.
 *
 * design.md §7.5, §7.6. Everything here is chrome for a screen whose real content is the page
 * underneath it, so the governing constraint is *how little of the frame it may take*: §5.1's frame
 * is a fixed-size non-scrolling box measured in pixels, and a bar that grew would take the growth out
 * of the text. So the bar is one line, the chat is a sheet over it, and the two things that are
 * allowed to interrupt — "Tap to join" and "waiting for Karti" — are the two the room cannot
 * function without.
 *
 * ── This file owns no stylesheet ────────────────────────────────────────────
 * Inline styles with tokens, for the reason `PlaybackBar.tsx` and `Scrubber.tsx` already state:
 * `ui/app.css` and `reader-page.css` belong to other sessions' diffs, and a component that reaches
 * into a stylesheet it does not own is a merge conflict with a rendering bug in it. Every colour,
 * space and radius here is a `--token`, so the four themes keep working without this file knowing
 * they exist.
 *
 * ── The refusals ───────────────────────────────────────────────────────────
 * A member who may not drive the room gets a control that is **absent or plainly inert**, never one
 * that looks live and does nothing. §7.1's server re-clamps everything anyway; the purpose of
 * `canControl` here is so a person can tell, before they press it, that the remote is somebody
 * else's — and `passRemote` is one tap away in the member list, which is the answer to "why can't I".
 */

import { useState, type CSSProperties, type ReactElement } from "react";

import type { BookPos, PlaybackMode } from "../../../shared/position.ts";
import {
  BLOCK_DEADLINE_MS,
  initialsOf,
  memberOf,
  roomShareUrl,
  type ChatMessage,
  type NotReady,
  type Playback,
  type RoomMember,
  type RoomSnapshot,
} from "../sync/room.ts";

/* ══════════════════════════════════════════════════════════════════════════
 * Copy
 * ══════════════════════════════════════════════════════════════════════════ */

/**
 * What a block says, in the words a person reads. Pure, and tested.
 *
 * ⚠️ **`generating` is the one that must not read as an error**, because it is the normal state of a
 * freshly uploaded book and it resolves itself. §4.9's line — *"generating the next page — about 12
 * seconds"* — is the shape: it names the person, names the wait, and gives a number when there is an
 * honest one to give. When there is not, it says so rather than inventing "a few seconds", which is
 * the sentence that is still on screen ninety seconds later.
 *
 * ⚠️ **`gesture` never blames the person.** They have not done anything wrong; their browser refused
 * to start audio without a tap, which is a correct browser doing its job. "Waiting for Karti to tap
 * to join" is a fact; "Karti is not ready" is an accusation about somebody who is sitting there
 * looking at a Tap to join button.
 */
export function blockCopy(reason: NotReady, name: string, etaSec?: number | null): string {
  /**
   * ⚠️ **"you" is not a name and does not take an apostrophe-s.** The caller passes the literal
   * `"you"` when the block is this member's own, and every possessive below then read *"Making the
   * voice for you's next page"* — photographed at 390 px during P5's gate walk, on the banner the
   * gate exists to put in front of four people at once. `their` for the same reason: "Waiting for
   * you — their audio is still loading" is about somebody else.
   */
  const me = name === "you";
  const whose = me ? "your" : `${name}'s`;
  const theirs = me ? "your" : "their";

  switch (reason) {
    case "buffering":
      return `Waiting for ${name} — ${theirs} audio is still loading.`;
    case "generating":
      return etaSec != null && Number.isFinite(etaSec) && etaSec > 0
        ? `Making the voice for ${whose} next page — about ${Math.round(etaSec)} seconds.`
        : `Making the voice for ${whose} next page.`;
    case "gesture":
      return `Waiting for ${name} to tap to join.`;
    case "behind":
      return `Waiting for ${name} to catch up.`;
  }
}

/**
 * LAW 20's deadline, rendered as reassurance rather than as a countdown.
 *
 * A visible timer on a shared banner turns a 40-second wait into 40 seconds of watching a number,
 * and the deadline exists to bound the *failure*, not to be the interface. What a person needs to
 * know is that this will end by itself, so that is what it says.
 */
export function blockDeadlineCopy(reason: NotReady): string | null {
  const ms = BLOCK_DEADLINE_MS[reason];
  return ms === null ? null : "This stops waiting on its own.";
}

/* ══════════════════════════════════════════════════════════════════════════
 * Styling primitives
 * ══════════════════════════════════════════════════════════════════════════ */

const bar: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: "var(--space-2)",
  minHeight: "var(--tap-min)",
  maxWidth: "100vw",
  overflowX: "hidden",
  padding: "var(--space-1) var(--space-3) max(var(--space-1), env(safe-area-inset-bottom))",
  fontFamily: "var(--font-ui)",
  fontSize: "0.8125rem",
  color: "var(--ink-muted)",
  borderTop: "1px solid var(--rule-hair)",
  background: "var(--sheet)",
};

const ghostBtn: CSSProperties = {
  display: "inline-flex",
  flex: "0 0 auto",
  alignItems: "center",
  gap: "var(--space-1)",
  minHeight: "var(--tap-min)",
  padding: "0 var(--space-2)",
  border: "1px solid transparent",
  borderRadius: "var(--radius)",
  background: "transparent",
  color: "var(--ink)",
  font: "inherit",
  whiteSpace: "nowrap",
  cursor: "pointer",
};

const chipBtn: CSSProperties = {
  ...ghostBtn,
  minHeight: "1.75rem",
  border: "1px solid var(--rule)",
  borderRadius: "var(--radius-pill)",
  padding: "0 var(--space-2)",
  fontSize: "0.75rem",
};

/* ══════════════════════════════════════════════════════════════════════════
 * Members
 * ══════════════════════════════════════════════════════════════════════════ */

export interface MemberChipProps {
  member: RoomMember;
  isMe: boolean;
  hasRemote: boolean;
}

/**
 * One member.
 *
 * §7.6: *"Guest names are sanitised … and always rendered with a distinct chip and dotted ring — a
 * guest can never look like an account."* The dotted ring is the whole mechanism and it is not
 * decoration: movies' own comment records that a member's name used to be whatever the browser
 * claimed, so anyone could sit in a theater as anyone. The server refuses a guest name that
 * normalises to a signed-in member's handle; this is the second line of that defence, and it is the
 * one a person actually sees.
 *
 * A disconnected member is dimmed, **not removed** — §7.6, membership is durable. A phone that locks
 * must keep its seat visibly, or every screen lock reads as somebody leaving.
 */
export function MemberChip({ member, isMe, hasRemote }: MemberChipProps): ReactElement {
  return (
    <span
      title={`${member.name}${member.kind === "guest" ? " (guest)" : ""}${hasRemote ? " · has the remote" : ""}`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "0.25rem",
        padding: "0.0625rem 0.375rem 0.0625rem 0.0625rem",
        borderRadius: "var(--radius-pill)",
        border: member.kind === "guest" ? "1px dashed var(--rule-interactive)" : "1px solid transparent",
        background: member.connected ? "var(--hover)" : "transparent",
        opacity: member.connected ? 1 : 0.45,
        fontSize: "0.75rem",
        color: "var(--ink)",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: "1.125rem",
          height: "1.125rem",
          borderRadius: "var(--radius-sm)",
          background: member.color,
          color: "var(--ink-inverse)",
          display: "grid",
          placeItems: "center",
          fontSize: "0.5625rem",
          fontWeight: 600,
        }}
      >
        {initialsOf(member.name)}
      </span>
      <span>
        {member.name}
        {isMe ? " (you)" : ""}
      </span>
      {hasRemote ? (
        <span aria-label="has the remote" title="has the remote" style={{ opacity: 0.7 }}>
          ⏵
        </span>
      ) : null}
    </span>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
 * The bar
 * ══════════════════════════════════════════════════════════════════════════ */

export interface RoomBarProps {
  room: RoomSnapshot;
  playback: Playback | null;
  myKey: string | null;
  canControl: boolean;
  connected: boolean;
  /** LAW 12's flag for this reader. False after a manual turn. */
  following: boolean;
  onFollow(on: boolean): void;
  /** §7.1's local-only "put me back where they are". */
  onResync(): void;
  onLeave(): void;
  /**
   * §7.6: close the room for everybody. Rendered **only for a host**, and only as the second of two
   * separate actions — "Leave" and "Close" are different intentions and one button that guesses
   * between them is how somebody ends the Tuesday room by tapping the wrong thing.
   *
   * It is also §6.5's escape hatch: a persistent room is never garbage-collected, so a quota that
   * counts un-ended rooms has no way down without this.
   */
  onEnd(): void;
  onOpenChat(): void;
  unreadChat: number;
  onReact(emoji: string): void;
  /** For the copyable `/r/:code` link (§7.6). Injected so the test does not need `location`. */
  origin: string;
}

export function RoomBar(props: RoomBarProps): ReactElement {
  const { room, playback, myKey, canControl, connected, following } = props;
  const [copied, setCopied] = useState(false);

  const connectedMembers = room.members.filter((m) => m.connected);
  // `holdsRemote` is computed by the server with the same `canControl` the socket enforces with, so
  // the chip and the refusal cannot disagree. Deriving it here from `isHost` would be the second
  // implementation — and it would be wrong the moment somebody used `passRemote`, which is exactly
  // when a person most needs to see where the remote went.
  const remoteHolder = room.members.find((m) => m.key === room.remoteHolderKey) ?? null;

  /**
   * *Why* a control did nothing, before somebody presses it a second time.
   *
   * `canControl` is a courtesy (§7.1 re-clamps everything server-side), and its whole job is this
   * sentence: a member who taps play and gets silence has to be able to see, without asking, that
   * the remote is somebody else's — and who to ask for it. `everyone` says nothing at all, because
   * in that mode there is nothing to explain.
   */
  const remoteNote =
    canControl || room.controlMode === "everyone"
      ? null
      : remoteHolder && remoteHolder.key !== myKey
        ? `${remoteHolder.name} has the remote`
        : "The host has the remote";

  return (
    <div style={bar} role="region" aria-label="Reading together">
      {/* Connection first, because every other thing on this bar is a lie while it is down. A room
          whose members are frozen and whose bar looks normal is the worst of the states. */}
      <span
        aria-hidden="true"
        title={connected ? "Connected" : "Reconnecting…"}
        style={{
          width: "0.5rem",
          height: "0.5rem",
          borderRadius: "var(--radius-pill)",
          flex: "0 0 auto",
          background: connected ? "var(--presence-3)" : "var(--ink-faint)",
        }}
      />
      <span style={{ fontWeight: 600, color: "var(--ink)", whiteSpace: "nowrap" }}>{room.name}</span>
      <span aria-hidden="true" style={{ opacity: 0.4 }}>
        ·
      </span>
      <span style={{ whiteSpace: "nowrap" }}>{playback?.mode === "read" ? "Reading" : "Listening"}</span>

      <span
        style={{
          display: "flex",
          gap: "var(--space-1)",
          overflowX: "auto",
          flex: "1 1 auto",
          flexBasis: "8rem",
          minWidth: 0,
          padding: "var(--space-1) 0",
        }}
      >
        {room.members.map((m) => (
          <MemberChip key={m.key} member={m} isMe={m.key === myKey} hasRemote={m.holdsRemote} />
        ))}
      </span>

      {remoteNote ? (
        <span style={{ whiteSpace: "nowrap", color: "var(--ink-faint)" }}>{remoteNote}</span>
      ) : null}

      {/* §7.4's read-mode pill, and §5.5's listen-mode one, are the same idea with different
          triggers: you turned a page and the room went on without you. It NEVER moves anybody by
          itself — pressing it is the whole interaction, and `onResync` emits no intent (§7.1). */}
      {!following ? (
        <button type="button" style={chipBtn} onClick={() => props.onResync()}>
          Back to the room
        </button>
      ) : null}

      <button
        type="button"
        style={chipBtn}
        aria-pressed={following}
        onClick={() => props.onFollow(!following)}
        title={following ? "Stop following the room" : "Follow the room again"}
      >
        {following ? "Following" : "Not following"}
      </button>

      <ReactionPicker onReact={props.onReact} />

      <button type="button" style={chipBtn} onClick={props.onOpenChat}>
        Chat{props.unreadChat > 0 ? ` · ${props.unreadChat}` : ""}
      </button>

      {/* §7.6: `/r/:code` is a shareable URL that joins on open. The code is shown as well as
          copied — half the time somebody is reading it out loud over the phone. */}
      <button
        type="button"
        style={chipBtn}
        onClick={() => {
          const url = roomShareUrl(room.code, props.origin);
          void navigator.clipboard
            ?.writeText(url)
            .then(() => setCopied(true))
            // No clipboard permission (or an insecure origin). The code is on screen either way,
            // which is why this failure is silent rather than an alert about a feature nobody asked
            // for.
            .catch(() => undefined);
        }}
        title={`Copy the link to this room (${connectedMembers.length} here)`}
      >
        {copied ? "Link copied" : room.code}
      </button>

      <button type="button" style={ghostBtn} onClick={props.onLeave} title="Leave the room">
        Leave
      </button>

      {/* Hosts only. `isHost` on the snapshot is the server's own answer (LAW 13) — the server
          re-checks the message, so this is which control to draw, never who may do it. */}
      {memberOf(room, myKey)?.isHost === true ? (
        <button
          type="button"
          style={ghostBtn}
          onClick={() => {
            // A confirm, because it ends the evening for four people and there is no undo: an ended
            // code is never handed out again, deliberately, so the chat and the seats do not come back.
            if (window.confirm(`Close “${room.name}” for everybody? The code stops working.`)) props.onEnd();
          }}
          title="Close this room for everybody"
        >
          Close
        </button>
      ) : null}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
 * The three interruptions
 * ══════════════════════════════════════════════════════════════════════════ */

export interface TapToJoinProps {
  roomName: string;
  onTap(): void;
}

/**
 * §7.5's `gesture`, as the only thing on the screen that can fix it.
 *
 * A browser will not start audio without a user gesture, and a remote `play` arriving at a tab
 * nobody has touched is refused. The failure mode without this overlay is the worst kind: the room
 * plays, this device is silent, and nothing anywhere says why.
 *
 * The whole surface is the button. A small "tap to enable audio" toast in a corner is missed by
 * exactly the person who needs it — the one who put their phone down, joined, and picked it up
 * again — and the 20 s block deadline means their silence holds the room up while they miss it.
 */
export function TapToJoin({ roomName, onTap }: TapToJoinProps): ReactElement {
  return (
    <button
      type="button"
      onClick={onTap}
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 40,
        display: "grid",
        placeItems: "center",
        gap: "var(--space-2)",
        border: 0,
        background: "color-mix(in srgb, var(--desk) 82%, transparent)",
        backdropFilter: "blur(2px)",
        font: "inherit",
        fontFamily: "var(--font-ui)",
        color: "var(--ink)",
        cursor: "pointer",
      }}
    >
      <span style={{ fontSize: "1.125rem", fontWeight: 600 }}>Tap to join {roomName}</span>
      <span style={{ fontSize: "0.8125rem", color: "var(--ink-muted)" }}>
        Your browser needs one tap before it will play sound.
      </span>
    </button>
  );
}

export interface BlockedBannerProps {
  blockedBy: NonNullable<Playback["blockedBy"]>;
  members: readonly RoomMember[];
  myKey: string | null;
  /** Local-only recovery for the deadline-free `behind` block. Emits no room control. */
  onResync?: (() => void) | null;
}

export function mayCatchUp(
  blockedBy: readonly { memberKey: string; reason: NotReady }[],
  myKey: string | null,
): boolean {
  return myKey !== null && blockedBy.some((b) => b.reason === "behind" && b.memberKey === myKey);
}

/**
 * *"…and both wait with an honest banner while the server queues it"* — P5's gate, literally.
 *
 * **Honest** is the operative word and it is why this renders the *reason* rather than a spinner: a
 * room that is waiting because a phone is on 3G and a room that is waiting because a GPU is busy are
 * two different situations, and only one of them is going to end in twelve seconds.
 *
 * Only *connected* members can appear here (§7.5), and that transfers from movies by luck and is
 * preserved here deliberately, because on this platform it is load-bearing: **iOS suspends a
 * backgrounded tab's WebSocket within ~30 s of screen lock while audio keeps playing.** If a
 * disconnected member could block, every phone that locks would freeze the room — which is the exact
 * failure P5's gate goes looking for.
 */
export function BlockedBanner({ blockedBy, members, myKey, onResync }: BlockedBannerProps): ReactElement | null {
  if (blockedBy.length === 0) return null;
  const first = blockedBy[0];
  if (!first) return null;
  const who = members.find((m) => m.key === first.memberKey);
  const name = first.memberKey === myKey ? "you" : (who?.name ?? "someone");
  const deadline = blockDeadlineCopy(first.reason);
  const iAmBehind = mayCatchUp(blockedBy, myKey);

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: "var(--space-2)",
        padding: "var(--space-2) var(--space-3)",
        fontFamily: "var(--font-ui)",
        fontSize: "0.8125rem",
        color: "var(--ink)",
        background: "var(--wash-narrating)",
        borderTop: "1px solid var(--rule-hair)",
      }}
    >
      <span>{blockCopy(first.reason, name, first.etaSec ?? null)}</span>
      {deadline ? <span style={{ color: "var(--ink-muted)" }}>{deadline}</span> : null}
      {blockedBy.length > 1 ? (
        <span style={{ color: "var(--ink-muted)" }}>+{blockedBy.length - 1} more</span>
      ) : null}
      {iAmBehind && onResync ? (
        <button type="button" style={chipBtn} onClick={onResync}>
          Catch up now
        </button>
      ) : null}
    </div>
  );
}

export interface ResumePromptProps {
  /** Where the room stopped. */
  roomAt: BookPos;
  since: number;
  roomLabel: string;
  /** Where *you* stopped, outside the room. Null when you have no personal position. */
  mineLabel: string | null;
  onResumeTogether(): void;
  onResumeMine(): void;
  onDismiss(): void;
}

/**
 * §7.6: *"The room bookmark and your personal bookmark are different rows and the app asks once."*
 *
 * ⚠️ **Nothing auto-plays and nothing auto-navigates.** Both are offered; neither is applied. Merging
 * them automatically silently destroys somebody's progress, and picking one for them is the same
 * thing with a friendlier name. The question is asked once per room and the answer is remembered.
 */
export function ResumePrompt(props: ResumePromptProps): ReactElement {
  const days = Math.max(0, Math.round((Date.now() - props.since) / 86_400_000));
  const when = days === 0 ? "earlier today" : days === 1 ? "yesterday" : `${days} days ago`;

  return (
    <div
      role="dialog"
      aria-label="Pick up where you stopped"
      style={{
        display: "grid",
        gap: "var(--space-2)",
        padding: "var(--space-3)",
        fontFamily: "var(--font-ui)",
        fontSize: "0.8125rem",
        background: "var(--sheet)",
        borderTop: "1px solid var(--rule)",
        boxShadow: "var(--shadow-proof)",
      }}
    >
      <span style={{ color: "var(--ink)" }}>Pick up where you stopped {when}?</span>
      <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
        <button type="button" style={chipBtn} onClick={props.onResumeTogether}>
          Resume together · {props.roomLabel}
        </button>
        {props.mineLabel ? (
          <button type="button" style={chipBtn} onClick={props.onResumeMine}>
            Where you left off · {props.mineLabel}
          </button>
        ) : null}
        <button type="button" style={ghostBtn} onClick={props.onDismiss}>
          Not now
        </button>
      </div>
    </div>
  );
}

export interface RoomFlashProps {
  flash: { seq: number; text: string; kind: "good" | "bad" | "info" };
  onDismiss(): void;
}

/**
 * One short notice, for one member. §7.5's honest state after a dropped block.
 *
 * ⚠️ **It does not expire on a timer, and that is deliberate.** This is how a member learns that the
 * room stopped waiting for them — *"the voice service is offline — you're reading ahead of the
 * narration"* — and a sentence that explains why the room is behaving differently has to still be
 * there when they look up from the page. movies' flashes auto-dismiss at 3 s because they announce
 * things that already happened on screen; this one announces something that did **not**.
 */
export function RoomFlash({ flash, onDismiss }: RoomFlashProps): ReactElement {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--space-2)",
        padding: "var(--space-2) var(--space-3)",
        fontFamily: "var(--font-ui)",
        fontSize: "0.8125rem",
        color: "var(--ink)",
        background: flash.kind === "bad" ? "var(--wash-select)" : "var(--hover)",
        borderTop: "1px solid var(--rule-hair)",
      }}
    >
      <span style={{ flex: "1 1 auto" }}>{flash.text}</span>
      <button type="button" style={{ ...ghostBtn, minHeight: "1.75rem" }} onClick={onDismiss}>
        OK
      </button>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
 * The furthest-point pill (§7.6) — offered, never applied
 * ══════════════════════════════════════════════════════════════════════════ */

export interface FurthestPillProps {
  /** "Chapter 14" — a chapter, never a page number (§5.1: page numbers are never persisted). */
  label: string;
  onJump(): void;
  onDismiss(): void;
}

/**
 * *"Jump to your furthest point · Chapter 14."*
 *
 * ⚠️ **This is the compensating affordance for last-write-wins, and it exists precisely so that the
 * merge can stay last-write-wins.** §7.6: cross-device position sync outside rooms is LWW by
 * `updatedAt`, **not furthest-wins** — re-reading a chapter legitimately moves you backwards, and
 * furthest-wins yanks you forward every time you open the app. `newerPosition()` in
 * `shared/position.ts` is the merge and `lib/position.ts` keeps the `furthest` row separate for this
 * one purpose.
 *
 * Dismissible, and it does not come back for that position: a person who has said "no, I meant to be
 * here" has answered the question, and asking again on the next page turn is the yank with extra
 * steps.
 */
export function FurthestPill({ label, onJump, onDismiss }: FurthestPillProps): ReactElement {
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "var(--space-1)",
        padding: "0.125rem 0.125rem 0.125rem var(--space-3)",
        borderRadius: "var(--radius-pill)",
        border: "1px solid var(--rule)",
        background: "var(--sheet)",
        boxShadow: "var(--shadow-sheet)",
        fontFamily: "var(--font-ui)",
        fontSize: "0.75rem",
      }}
    >
      <button type="button" style={{ ...ghostBtn, minHeight: "1.75rem", padding: 0 }} onClick={onJump}>
        Jump to your furthest point · {label}
      </button>
      <button
        type="button"
        aria-label="Dismiss"
        title="Dismiss"
        style={{ ...ghostBtn, minHeight: "1.75rem", padding: "0 var(--space-2)", color: "var(--ink-muted)" }}
        onClick={onDismiss}
      >
        ×
      </button>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
 * Reactions and chat
 * ══════════════════════════════════════════════════════════════════════════ */

/**
 * Six emoji, fixed.
 *
 * A picker with a search field is a keyboard over a book. These are the six that mean something
 * about a *sentence*, which is the only thing a reaction in this product can be about (§7.6:
 * reactions are `{emoji, sid}`).
 */
export const REACTIONS: readonly string[] = ["❤️", "😂", "😮", "😢", "🔥", "🙏"];

export function ReactionPicker({ onReact }: { onReact(emoji: string): void }): ReactElement {
  const [open, setOpen] = useState(false);
  return (
    <span style={{ position: "relative", display: "inline-flex" }}>
      <button type="button" style={chipBtn} onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        React
      </button>
      {open ? (
        <span
          style={{
            position: "absolute",
            bottom: "calc(100% + var(--space-1))",
            right: 0,
            display: "flex",
            gap: "var(--space-1)",
            padding: "var(--space-1)",
            borderRadius: "var(--radius)",
            border: "1px solid var(--rule)",
            background: "var(--sheet)",
            boxShadow: "var(--shadow-lift)",
            zIndex: 30,
          }}
        >
          {REACTIONS.map((e) => (
            <button
              key={e}
              type="button"
              aria-label={`React ${e}`}
              style={{ ...ghostBtn, minHeight: "2rem", padding: "0 var(--space-1)", fontSize: "1rem" }}
              onClick={() => {
                onReact(e);
                setOpen(false);
              }}
            >
              {e}
            </button>
          ))}
        </span>
      ) : null}
    </span>
  );
}

export interface RoomChatProps {
  messages: readonly ChatMessage[];
  /**
   * The room's members, for the guest ring.
   *
   * ⚠️ Needed because `ChatMessage` carries a `name` and a `color` but **no `kind`** — so the chat
   * line cannot tell on its own whether "Karti" is the account or a guest who claimed the name. The
   * server refuses a guest name that `normalizeForCompare`s to a signed-in member's (§7.6, and
   * `sanitizeGuestName` in shared/protocol.ts), and this ring is the second line of that defence:
   * the one a person actually sees. A chat line is exactly where an unmarked guest name would be
   * taken for an account.
   */
  members: readonly RoomMember[];
  myKey: string | null;
  onSend(text: string): void;
  onClose(): void;
}

/**
 * The chat sheet.
 *
 * §7.6: `chatHistory` arrives on join and is stored in `room_chat`, **so a persistent room's chat
 * survives a deploy** — which is the difference between a chat and a transcript, and the reason a
 * Tuesday room is worth returning to. Nothing here persists anything; this renders what the server
 * remembered.
 */
export function RoomChat({ messages, members, myKey, onSend, onClose }: RoomChatProps): ReactElement {
  const [draft, setDraft] = useState("");
  const kindOf = (memberKey: string | null): RoomMember["kind"] | null =>
    memberKey === null ? null : (members.find((m) => m.key === memberKey)?.kind ?? null);
  return (
    <div
      role="dialog"
      aria-label="Room chat"
      style={{
        display: "grid",
        gridTemplateRows: "1fr auto",
        gap: "var(--space-2)",
        maxHeight: "40vh",
        padding: "var(--space-3)",
        fontFamily: "var(--font-ui)",
        fontSize: "0.8125rem",
        background: "var(--sheet)",
        borderTop: "1px solid var(--rule)",
        boxShadow: "var(--shadow-proof)",
      }}
    >
      <div style={{ overflowY: "auto", display: "grid", gap: "var(--space-1)", alignContent: "end" }}>
        {messages.length === 0 ? (
          <span style={{ color: "var(--ink-faint)" }}>Nothing said yet.</span>
        ) : (
          messages.map((m) =>
            // A system line ("Dad joined") has no author and must not be drawn as if somebody said
            // it — `memberKey` is null on exactly those, which is why it is nullable in the protocol.
            m.system ? (
              <p key={m.id} style={{ margin: 0, color: "var(--ink-faint)", fontStyle: "italic" }}>
                {m.text}
              </p>
            ) : (
              <p key={m.id} style={{ margin: 0 }}>
                <span
                  style={{
                    fontWeight: 600,
                    // The member's own colour, so one person is one colour in the chat, the margin
                    // dot and the scrubber tick. Assigned by the room, never by a component.
                    color: m.memberKey === myKey ? "var(--ink)" : m.color,
                    // The guest marker travels with the name everywhere it appears (§7.6).
                    borderBottom:
                      kindOf(m.memberKey) === "guest" ? "1px dashed var(--rule-interactive)" : "none",
                  }}
                >
                  {m.name}
                </span>{" "}
                <span style={{ color: "var(--ink)" }}>{m.text}</span>
              </p>
            ),
          )
        )}
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const t = draft.trim();
          if (!t) return;
          onSend(t);
          setDraft("");
        }}
        style={{ display: "flex", gap: "var(--space-2)" }}
      >
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Say something"
          maxLength={500}
          style={{
            flex: "1 1 auto",
            minHeight: "var(--tap-min)",
            padding: "0 var(--space-2)",
            border: "1px solid var(--rule-interactive)",
            borderRadius: "var(--radius)",
            background: "var(--paper)",
            color: "var(--ink)",
            font: "inherit",
          }}
        />
        <button type="submit" style={chipBtn}>
          Send
        </button>
        <button type="button" style={ghostBtn} onClick={onClose}>
          Close
        </button>
      </form>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
 * Mode
 * ══════════════════════════════════════════════════════════════════════════ */

export const modeLabel = (mode: PlaybackMode): string =>
  mode === "read" ? "Reading together" : "Listening together";
